import React, {Suspense, useEffect, useMemo, useRef, useState, lazy} from 'react'
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import {connect} from 'react-redux';

import ModulePreview from '../../components/ModulePreview';
import {searchBox, search} from '../../drupalSettings';
import {FILTER_DATES, FILTER_DESTINATION} from '../../constants';
import filterChange from '../../actions/filterChange';
import {pushSearchInteract} from 'js/helpers/gtm';
import {isMobiles} from 'js/helpers/browser';
import StickyComponent from '../../components/StickyComponent';
import {useBrowser} from 'js/hooks/useBrowser';

import styles from './SearchModuleDestination.module.scss';

const ModuleDestinationSearch = lazy(() => import('./components/ModuleDestinationSearch'));

const HOTEL_SOURCE = 'hod_hotels';
const GEO_SOURCE = 'geomaj';
const GOOGLE_PLACES = 'google_places';
const SEARCH_STICKY_WRAP = 'search-sticky';
const cx = classnames.bind(styles);
const wordings = searchBox?.titles || {};
const sameDestination = search?.sameDestination;

const SearchModuleDestination = function ({
  className,
  activeFilter,
  filterChangeAction,
  isDatesExist,
  destination,
  onChange,
  urlParams,
}) {
  const browser = useBrowser();
  const [value, setValue] = useState(destination);
  const toglerRef = useRef(null);

  const isActive = useMemo(() => activeFilter === FILTER_DESTINATION, [activeFilter]);

  const isMobileVersion = useMemo(function () {
    return isMobiles(browser);
  }, [browser]);

  const onDestinationPreviewUpdate = function (value) {
    setValue({name: value});
    if (!isActive) {
      filterChangeAction('activeFilter', FILTER_DESTINATION);
    }
  };

  const onDestinationPreviewSubmit = function () {
    onDestinationSubmit(value);
  };

  const onDestinationDropdownUpdate = function (data) {
    setValue(data);
  };

  const previewValues = useMemo(function () {
    return [{
      label: wordings.wantToLiveIn,
      value: value?.name,
      readOnly: isMobileVersion,
      id: 'destination-field',
      name: 'destination-field',
      onSubmit: onDestinationPreviewSubmit,
      onChange: onDestinationPreviewUpdate,
    }];
  }, [value, isMobileVersion]);

  const onPreviewClick = function () {
    filterChangeAction('activeFilter', FILTER_DESTINATION);
    pushSearchInteract({
      action: 'open',
      field: 'destination'
    });
  };

  const onFormClose = function () {
    onDestinationBlur();
    filterChangeAction('activeFilter', null);
  }

  const saveDestinationUpdate = async function (data) {
    const isSameDestination = data.name === urlParams.q;
    return onChange({
      q: data.name,
      isAutocomplete: true,
      hotelDirectId: data.source === HOTEL_SOURCE ? data.id : '',
      geoCode: (data.source === GEO_SOURCE || data.source === GOOGLE_PLACES) ? data.id : '',
      geoType: (data.source === GEO_SOURCE || data.source === GOOGLE_PLACES) ? data.type : '',
      radius: isSameDestination ? (sameDestination?.radius || '') : '',
      unit: isSameDestination ? (sameDestination?.unit || '') : '',
      hotelId: data.hotelPageId || '',
    }, FILTER_DESTINATION);
  };

  const onDestinationSubmit = async function (data) {
    await onDestinationDropdownUpdate(data);
    await saveDestinationUpdate(data);
    await filterChangeAction('activeFilter', isDatesExist ? null : FILTER_DATES);
  };

  const onDestinationBlur = function () {
    if (!isMobileVersion) {
      saveDestinationUpdate(value);
    }
  }

  useEffect(() => {
    if (destination.name !== value.name) {
      setValue(destination);
    }
  }, [destination.name]);

  return (
    <div className={cx('root', className)}>
      { isMobileVersion && (
        <StickyComponent wrapEl={SEARCH_STICKY_WRAP} stickyClassName={cx('sticky-destination')}>
          <ModulePreview
            icon="GEOLOC"
            ref={toglerRef}
            values={previewValues}
            onClick={onPreviewClick}
          />
        </StickyComponent>
      )}
      {!isMobileVersion && (<ModulePreview
        icon="GEOLOC"
        ref={toglerRef}
        values={previewValues}
        onClick={onPreviewClick}
      />)}
      {isActive && (
        <Suspense>
          <ModuleDestinationSearch
            onClose={onFormClose}
            isMobile={isMobileVersion}
            value={value?.name || ''}
            submitOnClose={!isMobileVersion}
            onUpdate={onDestinationDropdownUpdate}
            onSubmit={onDestinationSubmit}
            togler={toglerRef.current}
          />
        </Suspense>
      )}
    </div>
  )
}

SearchModuleDestination.propTypes = {
  data: PropTypes.any
}

export default connect(({main, searchBox, url}) => ({
  activeFilter: searchBox?.activeFilter,
  isDatesExist: !!url?.uiParams?.dateIn,
  queryString: main.queryString,
  destination: {
    name: url?.uiParams.q,
    id: url?.uiParams?.hotelDirectId || url?.uiParams?.geoCode,
    type: url?.uiParams?.geoCode ? url?.uiParams?.geoType : undefined,
    source: url?.uiParams?.hotelDirectId ? HOTEL_SOURCE : (url?.uiParams?.geoType ? GEO_SOURCE : undefined),
    hotelPageId: url?.uiParams?.hotelId,
  },
  urlParams: url?.parameters,
}), {
  filterChangeAction: filterChange,
})(SearchModuleDestination);
